"use client"

import { useContext, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { ArrowLeft, Trophy, X, ChevronRight } from "lucide-react"
import { CircuitoContext } from "../../context/CircuitoContext"
import { DomainContext } from "./LayoutCircuito"
import "../../styles/RankingCircuito.css"

const TIPOS = [
  { value: "vaqueiro",      label: "Vaqueiro" },
  { value: "esteireiro",    label: "Esteireiro" },
  { value: "cavaloPuxada",  label: "Cavalo de Puxada" },
  { value: "cavaloEsteira", label: "Cavalo de Esteira" },
]

const TIPO_LABEL = Object.fromEntries(TIPOS.map((t) => [t.value, t.label]))

const MEDALS     = { 1: "🥇", 2: "🥈", 3: "🥉" }
const MEDAL_COLORS = {
  1: { accent: "var(--c-accent, #d4af37)", muted: "rgba(212,175,55,0.5)"  },
  2: { accent: "#c0c0c0",                  muted: "rgba(192,192,192,0.4)" },
  3: { accent: "#cd7f32",                  muted: "rgba(205,127,50,0.4)"  },
}

export default function CircuitoRanking() {
  const { circuito, loadingCircuito } = useContext(CircuitoContext)
  const { basePath }                  = useContext(DomainContext)

  const [categoriaSelecionada, setCategoriaSelecionada] = useState("")
  const [tipoSelecionado,      setTipoSelecionado]      = useState("vaqueiro")

  // ── Modal unificado ────────────────────────────────────────────────────────
  // modalGrupo: exibe lista de empatados de uma posição
  // modalPessoa: exibe histórico individual; tem "voltarGrupo" para o back-nav
  const [modalGrupo,  setModalGrupo]  = useState(null)   // { posicao, pontos, participantes, rank }
  const [modalPessoa, setModalPessoa] = useState(null)   // { pessoa, voltarGrupo }

  const fecharTudo       = ()  => { setModalGrupo(null); setModalPessoa(null) }
  const fecharPessoa     = ()  => { setModalPessoa(null) }
  const abrirPessoa      = (pessoa, grupo = null) =>
    setModalPessoa({ pessoa, voltarGrupo: grupo })

  const handlePodiumClick = (grupo) => {
    if (grupo.participantes.length === 1) {
      abrirPessoa(grupo.participantes[0])
    } else {
      setModalGrupo(grupo)
      setModalPessoa(null)
    }
  }

  // ── Filtro / ranking ───────────────────────────────────────────────────────
  useEffect(() => {
    if (circuito?.categorias?.length) {
      const primeira = circuito.categorias[0]
      setCategoriaSelecionada(primeira.nome || primeira)
    }
  }, [circuito])

  const participantesFiltrados = useMemo(() => {
    if (!circuito?.participantes) return []
    return circuito.participantes
      .filter((p) => p.categoria === categoriaSelecionada && p.tipo === tipoSelecionado)
      .sort((a, b) => {
        if (b.pontos !== a.pontos) return b.pontos - a.pontos
        const oa = a.ordemManual ?? 0, ob = b.ordemManual ?? 0
        if (oa !== ob) return oa - ob
        return new Date(a.updatedAt || 0) - new Date(b.updatedAt || 0)
      })
  }, [circuito, categoriaSelecionada, tipoSelecionado])

  // Agrupa por posição com empate (1, 2, 2, 4…)
  const grupos = useMemo(() => {
    const g = []
    participantesFiltrados.forEach((p, idx) => {
      const anterior = participantesFiltrados[idx - 1]
      if (anterior && anterior.pontos === p.pontos) {
        g[g.length - 1].participantes.push(p)
      } else {
        g.push({ posicao: idx + 1, pontos: p.pontos, participantes: [p] })
      }
    })
    return g
  }, [participantesFiltrados])

  const gruposPodio = grupos.slice(0, 3)
  const tabela      = grupos.slice(3).flatMap((grp) =>
    grp.participantes.map((p) => ({ ...p, posicao: grp.posicao }))
  )

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loadingCircuito) {
    return (
      <div className="rk-page">
        <div className="rk-loading"><div className="rk-spinner" /></div>
      </div>
    )
  }
  if (!circuito) return null

  const cssVars = {
    "--c-accent":  circuito.corPrimaria   || "#d4af37",
    "--c-hover":   circuito.corSecundaria || "#111",
    "--c-bg":      circuito.bgPrimario    || "#0f0f0f",
    "--c-card":    circuito.bgSecundario  || "#171717",
    "--c-text":    circuito.textoPrimario || "#ffffff",
    "--c-btn-txt": circuito.botaoTexto    || "#000000",
  }

  return (
    <div className="rk-page" style={cssVars}>
      <div className="rk-container">

        {/* Voltar */}
        <Link to={`${basePath}/`} className="rk-back">
          <ArrowLeft size={16} />
          <span>Voltar para o Circuito</span>
        </Link>

        {/* Cabeçalho */}
        <div className="rk-header">
          <div className="rk-header__icon"><Trophy size={28} /></div>
          <h1 className="rk-header__title">Ranking</h1>
          <p className="rk-header__subtitle">{circuito.nome}</p>
        </div>

        {/* Filtros */}
        <div className="rk-filters">
          <div className="rk-filter-group">
            <label>Categoria</label>
            <select
              className="rk-select"
              value={categoriaSelecionada}
              onChange={(e) => setCategoriaSelecionada(e.target.value)}
            >
              {circuito.categorias.map((cat, idx) => {
                const name = cat.nome || cat
                return <option key={idx} value={name}>{name}</option>
              })}
            </select>
          </div>
          <div className="rk-filter-group">
            <label>Tipo</label>
            <select
              className="rk-select"
              value={tipoSelecionado}
              onChange={(e) => setTipoSelecionado(e.target.value)}
            >
              {TIPOS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Sem resultados */}
        {participantesFiltrados.length === 0 && (
          <div className="rk-empty">
            <div className="rk-empty__icon">🏆</div>
            <p>Nenhum participante encontrado nesta categoria.</p>
          </div>
        )}

        {/* ── Pódio ──────────────────────────────────────────────────────── */}
        {participantesFiltrados.length > 0 && (
          <div className="rk-podium">
            {/* Ordem visual dos degraus: 2º | 1º | 3º */}
            {[
              { rank: 2, grupo: gruposPodio[1] },
              { rank: 1, grupo: gruposPodio[0] },
              { rank: 3, grupo: gruposPodio[2] },
            ].map(({ rank, grupo }) => {
              if (!grupo) return <div key={rank} className="rk-podium__item rk-podium__item--placeholder" />

              const empatados = grupo.participantes.length
              const primeiroNome = grupo.participantes[0].nome
              const extras = empatados - 1

              return (
                <div key={rank} className={`rk-podium__item rk-podium__item--${rank}`}>
                  <div
                    className="rk-podium__card"
                    onClick={() => handlePodiumClick({ ...grupo, rank })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handlePodiumClick({ ...grupo, rank })}
                  >
                    {/* Medalha flutuante */}
                    <div className="rk-podium__medal">{MEDALS[rank]}</div>

                    {/* Nome principal */}
                    <div className="rk-podium__name">{primeiroNome}</div>

                    {/* Pill de empate — só aparece quando há mais de 1 */}
                    {extras > 0 && (
                      <div className="rk-podium__empate-pill">
                        +{extras} empatado{extras > 1 ? "s" : ""}
                      </div>
                    )}

                    {/* Pontuação */}
                    <div className="rk-podium__score">
                      <span className="rk-podium__score-val">{grupo.pontos}</span>
                      <span className="rk-podium__score-lbl">pts</span>
                    </div>
                  </div>

                  {/* Degrau do pódio */}
                  <div className="rk-podium__base">{rank}°</div>
                </div>
              )
            })}
          </div>
        )}

        {/* ── Tabela (4º em diante) ──────────────────────────────────────── */}
        {tabela.length > 0 && (
          <div className="rk-table-wrap">
            <table className="rk-table">
              <thead>
                <tr>
                  <th style={{ textAlign: "center" }}>Pos.</th>
                  <th>Nome</th>
                  <th style={{ textAlign: "center" }}>Etapas</th>
                  <th style={{ textAlign: "right", paddingRight: 20 }}>Pontos</th>
                </tr>
              </thead>
              <tbody>
                {tabela.map((p, i) => (
                  <tr key={p._id || i} onClick={() => abrirPessoa(p)}>
                    <td className="rk-table__pos">{p.posicao}º</td>
                    <td className="rk-table__name">{p.nome}</td>
                    <td className="rk-table__etapas">{p.etapas}</td>
                    <td className="rk-table__pts" style={{ paddingRight: 20 }}>
                      <span className="rk-table__pts-val">
                        {p.pontos} <span className="rk-table__pts-lbl">pts</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL DE GRUPO — lista de empatados em uma posição do pódio
      ═══════════════════════════════════════════════════════════════════ */}
      {modalGrupo && !modalPessoa && (
        <div
          className="rk-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) fecharTudo() }}
        >
          <div className="rk-modal rk-modal--grupo" style={cssVars}>
            {/* Header */}
            <div className="rk-modal__header rk-modal__header--grupo">
              <button className="rk-modal__close" onClick={fecharTudo}>
                <X size={18} />
              </button>
              <div className="rk-modal__grupo-medal">{MEDALS[modalGrupo.rank]}</div>
              <h2 className="rk-modal__grupo-pos">{modalGrupo.rank}° lugar</h2>
              <div className="rk-modal__grupo-pts">
                <span>{modalGrupo.pontos}</span>
                <span className="rk-modal__grupo-pts-lbl"> pontos</span>
              </div>
              <p className="rk-modal__grupo-count">
                {modalGrupo.participantes.length} vaqueiros empatados
              </p>
            </div>

            {/* Lista */}
            <div className="rk-modal__grupo-lista">
              {modalGrupo.participantes.map((p, i) => (
                <button
                  key={p._id || i}
                  className="rk-modal__grupo-item"
                  onClick={() => abrirPessoa(p, modalGrupo)}
                >
                  <span className="rk-modal__grupo-num">{i + 1}</span>
                  <span className="rk-modal__grupo-nome">{p.nome}</span>
                  <div className="rk-modal__grupo-info">
                    <span className="rk-modal__grupo-etapas">{p.etapas} etapas</span>
                    <ChevronRight size={14} />
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          MODAL DE HISTÓRICO INDIVIDUAL
      ═══════════════════════════════════════════════════════════════════ */}
      {modalPessoa && (
        <div
          className="rk-modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) fecharTudo() }}
        >
          <div className="rk-modal" style={cssVars}>
            <div className="rk-modal__header">
              {/* Botão fechar (fecha tudo) */}
              <button className="rk-modal__close" onClick={fecharTudo}>
                <X size={18} />
              </button>

              {/* Botão voltar ao grupo (só aparece se veio de um grupo) */}
              {modalPessoa.voltarGrupo && (
                <button className="rk-modal__back" onClick={fecharPessoa}>
                  <ArrowLeft size={14} />
                  <span>Voltar</span>
                </button>
              )}

              <div className="rk-modal__tipo-badge">
                {TIPO_LABEL[modalPessoa.pessoa.tipo] || modalPessoa.pessoa.tipo}
              </div>
              <h2 className="rk-modal__name">{modalPessoa.pessoa.nome}</h2>
              <span className="rk-modal__categoria">{modalPessoa.pessoa.categoria}</span>
            </div>

            {/* Totalizadores */}
            <div className="rk-modal__stats">
              <div className="rk-modal__stat">
                <div className="rk-modal__stat-val">{modalPessoa.pessoa.pontos}</div>
                <div className="rk-modal__stat-lbl">Pontos totais</div>
              </div>
              <div className="rk-modal__stat">
                <div className="rk-modal__stat-val">{modalPessoa.pessoa.etapas}</div>
                <div className="rk-modal__stat-lbl">Etapas disputadas</div>
              </div>
            </div>

            {/* Histórico de etapas */}
            <div className="rk-modal__body">
              <p className="rk-modal__body-title">Histórico de Etapas</p>

              {!modalPessoa.pessoa.historico || modalPessoa.pessoa.historico.length === 0 ? (
                <p className="rk-modal__empty">Nenhuma etapa registrada ainda.</p>
              ) : (
                modalPessoa.pessoa.historico.map((etapa, idx) => (
                  <div key={idx} className="rk-etapa">
                    <div className="rk-etapa__header">
                      <div className="rk-etapa__dot" />
                      <span className="rk-etapa__nome">{etapa.nomeEtapa}</span>
                      {etapa.dataEtapa && (
                        <span className="rk-etapa__data">{etapa.dataEtapa}</span>
                      )}
                    </div>
                    <div className="rk-etapa__resultados">
                      {etapa.resultados.map((res, rIdx) => (
                        <div key={rIdx} className="rk-resultado">
                          <span className="rk-resultado__pos">{res.posicao}</span>
                          <span className="rk-resultado__pts">+{res.pontos} pts</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}